import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { 
  Loader2, 
  BrainCircuit, 
  RefreshCw, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2, 
  XCircle,
  User as UserIcon,
  MessageSquare,
  Cpu
} from 'lucide-react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from "../../components/ui/dialog";
import moment from 'moment';

interface AiLog {
  id: string;
  userId: string | null;
  type: string;
  prompt: string | null;
  response: string | null;
  model: string | null;
  tokensUsed: number | null;
  metadata: any;
  status: 'SUCCESS' | 'FAILED';
  errorMessage: string | null;
  createdAt: string;
  user?: {
    name: string;
    email: string;
  };
}

export default function AiLogs() {
  const [logs, setLogs] = useState<AiLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedLog, setSelectedLog] = useState<AiLog | null>(null);
  const token = localStorage.getItem('token');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const typeParam = filterType !== 'all' ? `?type=${filterType}` : '';
      const response = await axios.get(`${API_BASE_URL}/admin/ai-logs${typeParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching AI logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filterType]);

  const getStatusBadge = (status: string) => {
    if (status === 'SUCCESS') {
      return (
        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 gap-1 text-[10px] font-black uppercase">
          <CheckCircle2 className="h-2.5 w-2.5" />
          Success
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 gap-1 text-[10px] font-black uppercase">
        <XCircle className="h-2.5 w-2.5" />
        Failed
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden">
        <div className="h-1 bg-purple-500/50 w-full" />
        <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-2xl font-black tracking-tight">
              <BrainCircuit className="h-6 w-6 text-purple-400" />
              AI Interaction Logs
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Monitor prompts, responses and AI decision making processes.
            </CardDescription>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-background border border-border rounded-lg px-3 py-1">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="border-0 bg-transparent h-8 w-[180px] text-xs font-bold focus:ring-0">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border text-foreground">
                  <SelectItem value="all">All Interactions</SelectItem>
                  <SelectItem value="LANGUAGE_DETECTION">Language Detection</SelectItem>
                  <SelectItem value="TREND_ANALYSIS">Trend Analysis</SelectItem>
                  <SelectItem value="RECOMMENDATIONS">Recommendations</SelectItem>
                  <SelectItem value="TEXT_GENERATION">Content Assistant</SelectItem>
                  <SelectItem value="GRAMMAR_CHECK">Grammar Fixes</SelectItem>
                  <SelectItem value="REPHRASE">Rephrasing</SelectItem>
                  <SelectItem value="VIDEO_ANALYSIS">Video Analysis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={fetchLogs} 
              disabled={loading}
              className="border-border h-10 w-10"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="rounded-xl border border-border overflow-hidden bg-background/30">
            <Table>
              <TableHeader className="bg-card/50">
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Time</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Type</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">User</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Model</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Status</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px] text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-20">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-500 mx-auto" />
                      <p className="mt-2 text-muted-foreground text-xs font-medium">Loading AI history...</p>
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-20 text-muted-foreground text-sm italic">
                      No logs found for the selected criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="border-border hover:bg-card/30 transition-colors group">
                      <TableCell className="text-[10px] text-muted-foreground">
                        <div className="flex flex-col">
                          <span className="font-bold text-foreground">{moment(log.createdAt).format('HH:mm:ss')}</span>
                          <span className="text-[9px] text-muted-foreground">{moment(log.createdAt).format('MMM D')}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[9px] border-border text-muted-foreground bg-muted font-mono">
                          {log.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {log.user ? (
                          <div className="flex items-center gap-2">
                            <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center">
                              <UserIcon className="h-3 w-3 text-primary" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[10px] font-bold text-foreground">{log.user.name || 'Anonymous'}</span>
                              <span className="text-[8px] text-muted-foreground">{log.user.email}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">System</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground font-mono italic">
                        {log.model || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(log.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setSelectedLog(log)}
                          className="h-7 px-3 text-[10px] font-bold text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg"
                        >
                          View Inspect
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Inspector Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[800px] max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 bg-muted/50 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                  <BrainCircuit className="h-5 w-5 text-purple-400" />
                  Interaction Inspector
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  {selectedLog && moment(selectedLog.createdAt).format('LLLL')}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2 pr-8">
                {selectedLog && getStatusBadge(selectedLog.status)}
              </div>
            </div>
          </DialogHeader>
          
          <Tabs defaultValue="response" className="flex-1 flex flex-col min-h-0">
            <div className="px-6 pt-4 border-b border-border bg-muted/20">
              <TabsList className="bg-background border border-border p-1">
                <TabsTrigger value="response" className="text-xs data-[state=active]:bg-green-500/10 data-[state=active]:text-green-400">Response</TabsTrigger>
                <TabsTrigger value="prompt" className="text-xs data-[state=active]:bg-purple-500/10 data-[state=active]:text-purple-400">Prompt Sent</TabsTrigger>
                <TabsTrigger value="context" className="text-xs">Context & Meta</TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-6">
                  <TabsContent value="response" className="m-0 space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] uppercase font-black text-green-500/70 tracking-widest">Model Decision</h4>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-[9px] text-muted-foreground hover:text-foreground"
                          onClick={() => navigator.clipboard.writeText(selectedLog?.response || '')}
                        >
                          Copy Response
                        </Button>
                      </div>
                      <div className="p-6 bg-[#050811] rounded-xl border border-green-500/20 text-sm leading-relaxed text-green-400/90 whitespace-pre-wrap font-mono min-h-[100px]">
                        {selectedLog?.response || 'No response recorded'}
                      </div>
                    </div>

                    {selectedLog?.status === 'FAILED' && (
                      <div className="space-y-2">
                        <h4 className="text-[10px] uppercase font-black text-red-500 tracking-widest">Error Details</h4>
                        <div className="p-4 bg-red-500/5 rounded-xl border border-red-500/20 text-xs text-red-400 font-medium italic">
                          {selectedLog.errorMessage}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="prompt" className="m-0 space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] uppercase font-black text-muted-foreground tracking-widest">Prompt Structure</h4>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-[9px] text-muted-foreground hover:text-foreground"
                          onClick={() => navigator.clipboard.writeText(selectedLog?.prompt || '')}
                        >
                          Copy Prompt
                        </Button>
                      </div>
                      <div className="p-6 bg-background rounded-xl border border-border text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap font-sans">
                        {selectedLog?.prompt || 'No prompt recorded'}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="context" className="m-0 space-y-6">
                    {/* Metadata row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-3 bg-background rounded-xl border border-border space-y-1">
                        <span className="text-[9px] uppercase font-black text-muted-foreground tracking-widest flex items-center gap-1.5">
                          <Cpu className="h-3 w-3" /> Model
                        </span>
                        <p className="text-xs font-mono text-foreground">{selectedLog?.model || 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-background rounded-xl border border-border space-y-1">
                        <span className="text-[9px] uppercase font-black text-muted-foreground tracking-widest flex items-center gap-1.5">
                          <MessageSquare className="h-3 w-3" /> Tokens
                        </span>
                        <p className="text-xs font-mono text-foreground">{selectedLog?.tokensUsed || 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-background rounded-xl border border-border space-y-1">
                        <span className="text-[9px] uppercase font-black text-muted-foreground tracking-widest flex items-center gap-1.5">
                          <Filter className="h-3 w-3" /> Type
                        </span>
                        <p className="text-xs font-mono text-foreground">{selectedLog?.type}</p>
                      </div>
                    </div>

                    {/* Metadata JSON if exists */}
                    {selectedLog?.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-[10px] uppercase font-black text-muted-foreground tracking-widest">Execution Context</h4>
                        <pre className="p-4 bg-background rounded-xl border border-border text-[10px] font-mono text-primary/80 overflow-auto">
                          {JSON.stringify(selectedLog.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </TabsContent>
                </div>
              </ScrollArea>
            </div>
          </Tabs>
          
          <div className="p-4 bg-muted/50 border-t border-border flex justify-end pr-8">
            <Button onClick={() => setSelectedLog(null)} className="bg-card hover:bg-muted text-foreground">
              Close Inspector
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
